// Champs de formulaire communs : libellé visible au-dessus, cible de 44 px au
// moins, rien de pré-coché quand le choix engage (DA, accessibilité).
import type { InputHTMLAttributes, ReactNode } from 'react';

export interface Option<T extends string> {
  valeur: T;
  libelle: string;
  aide?: string;
}

interface ChoixProps<T extends string> {
  legende: string;
  nom: string;
  options: readonly Option<T>[];
  valeur: T;
  onChange: (v: T) => void;
  aide?: ReactNode;
}

export function Choix<T extends string>({
  legende,
  nom,
  options,
  valeur,
  onChange,
  aide,
}: Readonly<ChoixProps<T>>) {
  return (
    <fieldset className="mt-8">
      <legend className="font-semibold">{legende}</legend>
      {aide && <div className="text-secondaire mt-1 text-sm">{aide}</div>}
      <div className="mt-3 space-y-1">
        {options.map((o) => (
          <label key={o.valeur} className="flex min-h-11 cursor-pointer items-start gap-3 py-2">
            <input
              type="radio"
              name={nom}
              value={o.valeur}
              checked={valeur === o.valeur}
              onChange={() => onChange(o.valeur)}
              className="accent-accent mt-1 size-5 shrink-0"
            />
            <span>
              {o.libelle}
              {o.aide && <span className="text-secondaire block text-sm">{o.aide}</span>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

type ChampProps = InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  libelle: string;
  aide?: string;
};

export function Champ({ id, libelle, aide, className = '', ...props }: Readonly<ChampProps>) {
  return (
    <div className="mt-8">
      <label htmlFor={id} className="block font-semibold">
        {libelle}
      </label>
      {aide && (
        <p id={`${id}-aide`} className="text-secondaire mt-1 text-sm">
          {aide}
        </p>
      )}
      <input
        id={id}
        aria-describedby={aide ? `${id}-aide` : undefined}
        className={`border-filet bg-fond mt-2 min-h-11 rounded-lg border px-3 py-2 ${className}`}
        {...props}
      />
    </div>
  );
}

export function Case({
  id,
  libelle,
  aide,
  coche,
  onChange,
}: Readonly<{
  id: string;
  libelle: string;
  aide?: string;
  coche: boolean;
  onChange: (v: boolean) => void;
}>) {
  return (
    <div className="mt-8 flex items-start gap-3">
      <input
        id={id}
        type="checkbox"
        checked={coche}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-accent mt-1 size-5 shrink-0"
      />
      <label htmlFor={id}>
        <span className="font-semibold">{libelle}</span>
        {aide && <span className="text-secondaire block text-sm">{aide}</span>}
      </label>
    </div>
  );
}
